import React, { useState, useEffect } from 'react';
import { useNavigate } from '@tanstack/react-router';
import PhoneInput from 'react-phone-number-input';
import 'react-phone-number-input/style.css';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowRight, Check } from 'lucide-react';

export default function InternRegister() {
  const token = new URLSearchParams(window.location.search).get('token');
  
  const [step, setStep] = useState(1);
  const [status, setStatus] = useState('');
  const navigate = useNavigate();

  // Form State
  const [name, setName] = useState('');
  const [email, setEmail] = useState('');
  const [password, setPassword] = useState('');
  const [phone, setPhone] = useState('');
  const [panNumber, setPanNumber] = useState('');
  const [aadharNumber, setAadharNumber] = useState('');
  const [qualification, setQualification] = useState('');
  const [reasonToJoin, setReasonToJoin] = useState('');
  const [goals, setGoals] = useState('');

  // Handle Enter Key for quick navigation
  useEffect(() => {
    const handleKeyDown = (e) => {
      if (e.key === 'Enter' && !e.shiftKey) {
        // Only trigger if valid (basic check)
        const form = document.getElementById('register-form');
        if (form && form.checkValidity()) {
          e.preventDefault();
          handleNext(e);
        }
      }
    };
    window.addEventListener('keydown', handleKeyDown);
    return () => window.removeEventListener('keydown', handleKeyDown);
  }, [step]);

  const handleNext = (e) => {
    e.preventDefault();
    if (step < 3) {
      setStep(step + 1);
    } else {
      handleRegister();
    }
  };

  const handleRegister = async () => {
    try {
      const res = await fetch('https://hara-xy-worker.hara-xy.workers.dev/api/interns/register', {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ token, password, name, phone, panNumber, aadharNumber, qualification, reasonToJoin, goals })
      });
      const data = await res.json();
      if (res.ok) {
        setStatus('success');
        setTimeout(() => navigate({ to: '/intern/login' }), 3000);
      } else {
        setStatus(data.message || 'Registration failed');
      }
    } catch (e) {
      console.error(e);
      setStatus('An error occurred');
    }
  };

  if (!token) return (
    <div className="min-h-screen bg-[#050505] flex items-center justify-center text-white p-8">
      <div className="bg-white/5 backdrop-blur-xl border border-white/10 p-8 rounded-2xl shadow-2xl">
        <h2 className="text-2xl font-light">Invalid Registration Link</h2>
      </div>
    </div>
  );

  if (status === 'success') return (
    <div className="min-h-screen bg-[#050505] flex flex-col items-center justify-center text-white p-8 relative overflow-hidden">
      <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-[800px] h-[800px] bg-emerald-500/20 blur-[120px] rounded-full pointer-events-none" />
      <motion.div 
        initial={{ scale: 0.8, opacity: 0 }}
        animate={{ scale: 1, opacity: 1 }}
        transition={{ type: 'spring', damping: 20 }}
        className="relative z-10 flex flex-col items-center"
      >
        <div className="w-24 h-24 bg-emerald-500/20 rounded-full flex items-center justify-center mb-8 border border-emerald-500/50">
          <Check className="w-12 h-12 text-emerald-400" />
        </div>
        <h1 className="text-5xl font-light mb-4">You're all set!</h1>
        <p className="text-gray-400 text-xl font-light">Registration successful. Redirecting you to login...</p>
      </motion.div>
    </div>
  );

  return (
    <div className="min-h-screen bg-[#050505] text-white flex flex-col justify-center items-center px-6 py-12 md:px-12 relative overflow-hidden font-sans selection:bg-silver/30">
      
      {/* Premium Background Effects */}
      <div className="absolute top-0 left-0 w-full h-full overflow-hidden pointer-events-none">
        <div className="absolute -top-1/4 -left-1/4 w-[1000px] h-[1000px] bg-indigo-500/10 blur-[120px] rounded-full mix-blend-screen opacity-50" />
        <div className="absolute bottom-0 right-0 w-[800px] h-[800px] bg-blue-500/10 blur-[100px] rounded-full mix-blend-screen opacity-50" />
        <div className="absolute top-1/2 left-1/2 -translate-x-1/2 -translate-y-1/2 w-full h-full bg-[url('https://grainy-gradients.vercel.app/noise.svg')] opacity-[0.03]" />
      </div>

      <div className="w-full max-w-2xl mx-auto relative z-10 flex flex-col justify-center">
        
        {/* Progress Bar */}
        <div className="mb-10">
          <div className="h-1 bg-white/5 rounded-full overflow-hidden shadow-inner">
            <motion.div 
              className="h-full bg-gradient-to-r from-silver to-white shadow-[0_0_15px_rgba(255,255,255,0.5)]"
              initial={{ width: '33%' }}
              animate={{ width: `${(step / 3) * 100}%` }}
              transition={{ duration: 0.6, ease: [0.22, 1, 0.36, 1] }}
            />
          </div>
          <div className="text-gray-500 text-xs mt-4 font-mono uppercase tracking-[0.2em]">
            Step {step} of 3
          </div>
        </div>

        <form id="register-form" onSubmit={handleNext} className="space-y-8">
          <AnimatePresence mode="wait">
            
            {step === 1 && (
              <motion.div
                key="step1"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-8"
              >
                <div>
                  <h1 className="text-4xl md:text-5xl font-light mb-4 tracking-tight">Welcome to <span className="font-semibold bg-clip-text text-transparent bg-gradient-to-r from-white to-gray-500">HARA-XY</span>.</h1>
                  <p className="text-gray-400 text-lg md:text-xl font-light">Let's get you set up. Please provide your basic details.</p>
                </div>

                <div className="space-y-8">
                  <div className="group">
                    <label className="block text-xl md:text-2xl text-white/90 mb-2 font-light transition-colors group-focus-within:text-white">What's your full name?</label>
                    <input 
                      type="text"
                      value={name}
                      onChange={e => setName(e.target.value)}
                      required
                      minLength={2}
                      placeholder="Jane Doe"
                      className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-2xl md:text-3xl text-white focus:outline-none focus:border-silver transition-all placeholder:text-white/10 focus:shadow-[0_4px_20px_-10px_rgba(255,255,255,0.2)]"
                    />
                  </div>
                  <div className="group">
                    <label className="block text-xl md:text-2xl text-white/90 mb-2 font-light transition-colors group-focus-within:text-white">Confirm your email address</label>
                    <input 
                      type="email"
                      value={email}
                      onChange={e => setEmail(e.target.value)}
                      required
                      placeholder="jane@example.com"
                      className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-2xl md:text-3xl text-white focus:outline-none focus:border-silver transition-all placeholder:text-white/10 focus:shadow-[0_4px_20px_-10px_rgba(255,255,255,0.2)]"
                    />
                  </div>
                  <div className="group">
                    <label className="block text-xl md:text-2xl text-white/90 mb-2 font-light transition-colors group-focus-within:text-white">Set a secure password</label>
                    <input 
                      type="password"
                      value={password}
                      onChange={e => setPassword(e.target.value)}
                      required
                      minLength={6}
                      placeholder="••••••••"
                      title="Password must be at least 6 characters long"
                      className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-2xl md:text-3xl text-white focus:outline-none focus:border-silver transition-all placeholder:text-white/10 focus:shadow-[0_4px_20px_-10px_rgba(255,255,255,0.2)]"
                    />
                  </div>
                </div>

                <div className="inline-flex items-center gap-3 bg-white/5 border border-white/10 px-5 py-4 rounded-xl text-sm text-gray-300 backdrop-blur-md">
                  <span className="flex h-2 w-2 rounded-full bg-emerald-500 shadow-[0_0_10px_rgba(16,185,129,0.5)]"></span>
                  <span><strong className="text-white">Remember:</strong> This is a one-time setup. Keep your password safe.</span>
                </div>
              </motion.div>
            )}

            {step === 2 && (
              <motion.div
                key="step2"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-8"
              >
                <div>
                  <h1 className="text-4xl md:text-5xl font-light mb-4 tracking-tight">Identity & Contact</h1>
                  <p className="text-gray-400 text-lg md:text-xl font-light">We need a few more details to verify your identity.</p>
                </div>

                <div className="space-y-8">
                  <div className="group">
                    <label className="block text-xl md:text-2xl text-white/90 mb-2 font-light transition-colors group-focus-within:text-white">What's your phone number?</label>
                    <div className="border-b border-white/20 py-2 focus-within:border-silver transition-all focus-within:shadow-[0_4px_20px_-10px_rgba(255,255,255,0.2)]">
                      <PhoneInput
                        international
                        defaultCountry="IN"
                        value={phone}
                        onChange={setPhone}
                        required
                        className="text-2xl md:text-3xl text-white custom-phone-input"
                        style={{ '--PhoneInputCountrySelectArrowColor': 'rgba(255,255,255,0.5)' }}
                      />
                    </div>
                    <style dangerouslySetInnerHTML={{__html: `
                      .custom-phone-input input {
                        background: transparent !important;
                        color: white !important;
                        border: none !important;
                        outline: none !important;
                        padding-left: 1rem;
                      }
                      .custom-phone-input select { color: black !important; }
                    `}} />
                  </div>
                  <div className="group">
                    <label className="block text-xl md:text-2xl text-white/90 mb-2 font-light transition-colors group-focus-within:text-white">What is your PAN number?</label>
                    <input 
                      type="text"
                      value={panNumber}
                      onChange={e => setPanNumber(e.target.value.toUpperCase())}
                      required
                      minLength={10}
                      maxLength={10}
                      placeholder="ABCDE1234F"
                      className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-2xl md:text-3xl text-white focus:outline-none focus:border-silver transition-all placeholder:text-white/10 uppercase focus:shadow-[0_4px_20px_-10px_rgba(255,255,255,0.2)]"
                    />
                  </div>
                  <div className="group">
                    <label className="block text-xl md:text-2xl text-white/90 mb-2 font-light transition-colors group-focus-within:text-white">What is your Aadhar number?</label>
                    <input 
                      type="text"
                      value={aadharNumber}
                      onChange={e => setAadharNumber(e.target.value)}
                      required
                      minLength={12}
                      maxLength={12}
                      pattern="\d{12}"
                      placeholder="123456789012"
                      className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-2xl md:text-3xl text-white focus:outline-none focus:border-silver transition-all placeholder:text-white/10 focus:shadow-[0_4px_20px_-10px_rgba(255,255,255,0.2)]"
                    />
                  </div>
                </div>
              </motion.div>
            )}

            {step === 3 && (
              <motion.div
                key="step3"
                initial={{ opacity: 0, x: 20 }}
                animate={{ opacity: 1, x: 0 }}
                exit={{ opacity: 0, x: -20 }}
                transition={{ duration: 0.5, ease: [0.22, 1, 0.36, 1] }}
                className="space-y-8"
              >
                <div>
                  <h1 className="text-4xl md:text-5xl font-light mb-4 tracking-tight">Your Goals.</h1>
                  <p className="text-gray-400 text-lg md:text-xl font-light">Tell us a bit about your background and aspirations.</p>
                </div>

                <div className="space-y-8">
                  <div className="group">
                    <label className="block text-xl md:text-2xl text-white/90 mb-2 font-light transition-colors group-focus-within:text-white">What is your highest qualification?</label>
                    <input 
                      type="text"
                      value={qualification}
                      onChange={e => setQualification(e.target.value)}
                      required
                      minLength={2}
                      placeholder="e.g. B.Tech in Computer Science"
                      className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-2xl md:text-3xl text-white focus:outline-none focus:border-silver transition-all placeholder:text-white/10 focus:shadow-[0_4px_20px_-10px_rgba(255,255,255,0.2)]"
                    />
                  </div>
                  <div className="group">
                    <label className="block text-xl md:text-2xl text-white/90 mb-2 font-light transition-colors group-focus-within:text-white">Why do you want to join us?</label>
                    <textarea 
                      value={reasonToJoin}
                      onChange={e => setReasonToJoin(e.target.value)}
                      required
                      minLength={10}
                      placeholder="I am passionate about..."
                      className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-xl md:text-2xl text-white focus:outline-none focus:border-silver transition-all placeholder:text-white/10 min-h-[60px] resize-none focus:shadow-[0_4px_20px_-10px_rgba(255,255,255,0.2)]"
                    />
                  </div>
                  <div className="group">
                    <label className="block text-xl md:text-2xl text-white/90 mb-2 font-light transition-colors group-focus-within:text-white">What do you want to achieve here?</label>
                    <textarea 
                      value={goals}
                      onChange={e => setGoals(e.target.value)}
                      required
                      minLength={10}
                      placeholder="My goal is to build..."
                      className="w-full bg-transparent border-b border-white/20 px-0 py-3 text-xl md:text-2xl text-white focus:outline-none focus:border-silver transition-all placeholder:text-white/10 min-h-[60px] resize-none focus:shadow-[0_4px_20px_-10px_rgba(255,255,255,0.2)]"
                    />
                  </div>
                </div>
              </motion.div>
            )}
          </AnimatePresence>

          <div className="flex flex-wrap items-center gap-6 pt-8 pb-12">
            <button 
              type="submit" 
              className="px-8 py-4 bg-white text-black font-semibold rounded-2xl hover:bg-silver hover:shadow-[0_0_30px_rgba(255,255,255,0.3)] hover:-translate-y-1 transition-all duration-300 text-lg flex items-center justify-center gap-3"
            >
              {step < 3 ? 'OK' : 'Submit'} <ArrowRight className="w-5 h-5" />
            </button>
            <div className="text-gray-500 font-light flex items-center gap-2">
              press <strong className="font-semibold text-gray-300 bg-white/10 px-2 py-1 rounded">Enter ↵</strong>
            </div>
            
            {step > 1 && (
              <button 
                type="button" 
                onClick={() => setStep(step - 1)}
                className="ml-auto px-6 py-4 text-gray-400 font-medium hover:text-white transition-colors flex items-center justify-center"
              >
                Go back
              </button>
            )}
          </div>
          
          {status && status !== 'success' && (
            <motion.div 
              initial={{ opacity: 0, y: 10 }}
              animate={{ opacity: 1, y: 0 }}
              className="bg-red-500/10 border border-red-500/30 text-red-400 p-4 rounded-xl text-center backdrop-blur-md"
            >
              {status}
            </motion.div>
          )}
        </form>
      </div>
    </div>
  );
}
